import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, money } from "@/lib/api";

const METHODS = ["Bank transfer", "Wire transfer", "Debit card", "Other"];

export default function FundsDialog({ open, onOpenChange, mode, available, onDone }) {
  const [form, setForm] = useState({ amount: "", method: METHODS[0], note: "" });
  const [busy, setBusy] = useState(false);
  const isDeposit = mode === "deposit";
  useEffect(() => { if (open) setForm({ amount: "", method: METHODS[0], note: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/transactions/${isDeposit ? "deposit" : "withdraw"}`, { ...form, amount: Number(form.amount) });
      toast.success(`${isDeposit ? "Deposit" : "Withdrawal"} request submitted — pending approval`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-line bg-panel sm:max-w-md" data-testid={`${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isDeposit ? "Deposit funds" : "Withdraw funds"}</DialogTitle>
          <DialogDescription>
            {isDeposit ? "Submit a deposit request. Your balance is credited once our team approves it." : <>Available to withdraw: <span className="font-mono text-white">{money(available)}</span></>}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="funds-amount">Amount (USD)</Label>
            <Input id="funds-amount" type="number" inputMode="decimal" min="1" step="0.01" required value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} className="field font-mono" data-testid={`${mode}-amount-input`} />
          </div>
          <div className="space-y-2">
            <Label>Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="field" data-testid={`${mode}-method-select`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m} value={m} data-testid={`${mode}-method-${m.toLowerCase().replace(/\s+/g, "-")}`}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="funds-note">{isDeposit ? "Reference / note (optional)" : "Payout details / note (optional)"}</Label>
            <Textarea id="funds-note" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="rounded-xl border-line bg-surface" data-testid={`${mode}-note-input`} />
          </div>
          <button type="submit" disabled={busy} className={`${isDeposit ? "btn-primary" : "btn-gold"} w-full`} data-testid={`${isDeposit ? "deposit" : "withdraw"}-request-submit-button`}>
            {busy ? "Submitting..." : "Submit request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
